import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, FileText, Clock, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MemberLayout from "@/components/layout/MemberLayout";

function toAbsoluteUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function VideoPlayer({ url, poster, allowDownload }) {
  if (!url) {
    return (
      <div className="aspect-video rounded-2xl flex flex-col items-center justify-center gap-3 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(201,169,122,0.08), rgba(184,153,104,0.14))", border: "1px solid var(--border-light)" }} data-testid="lesson-empty-state">
        {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="relative z-10 text-center">
          <PlayCircle size={44} className="mx-auto mb-3 opacity-60" style={{ color: "var(--copper)" }} />
          <p className="font-serif text-lg" style={{ color: "var(--text-primary)" }}>Vídeo em breve</p>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Esta aula ainda não possui vídeo disponível.</p>
        </div>
      </div>
    );
  }

  // YouTube URL detection
  const ytMatch = url.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]+)/);
  if (ytMatch) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden bg-black">
        <iframe src={`https://www.youtube.com/embed/${ytMatch[1]}`} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="video" />
      </div>
    );
  }
  // Vimeo
  const vmMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vmMatch) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden bg-black">
        <iframe src={`https://player.vimeo.com/video/${vmMatch[1]}`} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="video" />
      </div>
    );
  }
  // Direct video file (mp4/webm) — hosted on object storage
  const fullSrc = toAbsoluteUrl(url);
  return (
    <div className="aspect-video rounded-2xl overflow-hidden bg-black relative">
      <video
        key={fullSrc}
        src={fullSrc}
        poster={poster || undefined}
        controls
        preload="metadata"
        playsInline
        controlsList={allowDownload ? undefined : "nodownload"}
        onContextMenu={allowDownload ? undefined : (e) => e.preventDefault()}
        className="w-full h-full"
        data-testid="lesson-video-native"
      />
    </div>
  );
}

export default function MemberLesson() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(false);

  const load = () => {
    api.get(`/member/lessons/${id}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.lesson.title} — Ana Paula Moraes`;
    });
  };

  useEffect(() => { load(); }, [id]);

  if (!data) return <MemberLayout><div className="p-8">Carregando…</div></MemberLayout>;

  const { lesson, course, completed } = data;
  const poster = lesson.thumbnail_url ? toAbsoluteUrl(lesson.thumbnail_url) : "";
  const isDirectVideo = lesson.video_url && !/(youtu\.?be|vimeo\.com)/i.test(lesson.video_url);
  const canDownload = !!lesson.allow_download && isDirectVideo;

  const toggleComplete = async () => {
    setMarking(true);
    try {
      await api.post("/member/progress", { lesson_id: lesson.id, completed: !completed });
      toast.success(!completed ? "Aula marcada como concluída!" : "Aula reaberta.");
      load();
    } finally {
      setMarking(false);
    }
  };

  return (
    <MemberLayout>
      <div className="p-6 lg:p-12 max-w-5xl mx-auto">
        <Link to={`/membros/curso/${course.id}`} className="inline-flex items-center gap-2 text-sm mb-6 hover:text-[color:var(--copper)]" style={{ color: "var(--text-secondary)" }} data-testid="lesson-back">
          <ArrowLeft size={14} /> {course.title}
        </Link>

        <VideoPlayer url={lesson.video_url} poster={poster} allowDownload={canDownload} />

        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="eyebrow mb-2 flex items-center gap-3 flex-wrap">
              <span>Aula</span>
              {lesson.duration && (
                <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  <Clock size={11} /> {lesson.duration}
                </span>
              )}
            </div>
            <h1 className="font-serif text-3xl md:text-4xl mb-4 leading-tight" style={{ color: "var(--text-primary)" }} data-testid="lesson-title">{lesson.title}</h1>
            {lesson.description && (
              <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--text-secondary)" }}>{lesson.description}</p>
            )}

            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={toggleComplete} disabled={marking} className={completed ? "btn-secondary" : "btn-primary"} data-testid="lesson-mark-complete">
                {completed ? (<><Check size={16} /> Concluída</>) : "Marcar como concluída"}
              </button>
              {canDownload && (
                <a
                  href={toAbsoluteUrl(lesson.video_url)}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                  data-testid="lesson-download-video"
                >
                  <Download size={16} /> Baixar vídeo
                </a>
              )}
            </div>
          </div>

          <aside className="brand-card p-6">
            <div className="eyebrow mb-3">Materiais de apoio</div>
            {lesson.materials && lesson.materials.length > 0 ? (
              <ul className="space-y-2">
                {lesson.materials.map((m, i) => (
                  <li key={i}>
                    <a href={toAbsoluteUrl(m.url)} target="_blank" rel="noreferrer" download className="flex items-center gap-2 text-sm hover:text-[color:var(--copper)]" style={{ color: "var(--text-primary)" }} data-testid={`lesson-material-${i}`}>
                      <FileText size={14} /> <span className="truncate">{m.name}</span>
                      <Download size={12} className="ml-auto flex-shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Nenhum material complementar.</p>
            )}
          </aside>
        </div>
      </div>
    </MemberLayout>
  );
}
